<div class="container flex flex-col mx-auto gap-4 px-4 md:px-6 pt-8 pb-4">
  <div class="flex flex-wrap items-center gap-2 min-h-10">
    <h2 class="text-2xl font-medium">Overzicht</h2>
  </div>

  <div class="flex flex-wrap items-center gap-2">
    <mat-chip-listbox
      (change)="handleDatePickerOptionChange($event)"
      hideSingleSelectionIndicator
      class="inline-flex! py-1 px-2 bg-white rounded-full [--mdc-chip-outline-color:transparent] [--mdc-chip-container-shape-radius:24px] [--mdc-chip-label-text-color:var(--color-gray-500)] [--mdc-chip-elevated-selected-container-color:var(--color-secondary-200)] [--mdc-chip-selected-label-text-color:var(--color-secondary-800)]"
    >
      @for (option of dateRangeOptions(); track $index) {
        <mat-chip-option
          [value]="option.value"
          [selected]="option.value === dateRangeOptionSelected()"
          [selectable]="option.value !== dateRangeOptionSelected()"
          [matTooltip]="option.description"
          >{{ option.value | uppercase }}</mat-chip-option
        >
      }
    </mat-chip-listbox>

    <!-- Custom Date Range Picker -->
    <div class="relative inline-block">
      <div
        class="inline-flex items-center gap-1 p-1 pl-4 bg-white rounded-full cursor-default"
      >
        <span class="text-sm text-gray-500 font-medium select-none">
          {{ formatDateRange() }}
        </span>

        <div class="relative">
          <mat-date-range-input
            [formGroup]="dateRangePicker"
            [rangePicker]="picker"
            [min]="minDate"
            [max]="maxDate"
            class="opacity-0 pointer-events-none absolute inset-0"
          >
            <input matStartDate formControlName="start" />
            <input matEndDate formControlName="end" />
          </mat-date-range-input>

          <button
            mat-icon-button
            (click)="picker.open()"
            class="inline-flex! items-center justify-center"
            matTooltip="Datumbereik kiezen"
          >
            <mat-icon class="size-5! text-xl! leading-none!">calendar_today</mat-icon>
          </button>
        </div>
      </div>

      <mat-date-range-picker #picker [xPosition]="'end'">
        <mat-datepicker-actions>
          <button mat-button matDatepickerCancel>Annuleren</button>
          <button
            mat-raised-button
            color="primary"
            matDatepickerApply
            (click)="onApplyClicked()"
          >
            Toepassen
          </button>
        </mat-datepicker-actions>
      </mat-date-range-picker>
    </div>
  </div>

  <div
    class="bg-white rounded-lg grid grid-rows-[repeat(2,auto)] md:grid-cols-2 lg:grid-cols-4"
  >
    <div class="p-8 grid grid-rows-subgrid row-span-2">
      <div class="text-sm font-medium text-gray-500">Geplande interacties</div>
      <div class="text-4xl font-medium mt-2">
        <app-counter
          [target]="numericCards().planned"
          [duration]="1000"
          easing="cubicInOut"
        />
      </div>
    </div>

    <div class="p-8 grid grid-rows-subgrid row-span-2">
      <div class="text-sm font-medium text-gray-500">Afgeronde interacties</div>
      <div class="text-4xl font-medium mt-2">
        <app-counter
          [target]="numericCards().completed"
          [duration]="1000"
          easing="cubicInOut"
        />
      </div>
    </div>

    <div class="p-8 grid grid-rows-subgrid row-span-2">
      <div class="text-sm font-medium text-gray-500">Niet afgeronde interacties</div>
      <div class="text-4xl font-medium mt-2">
        <app-counter
          [target]="numericCards().notCompleted"
          [duration]="1000"
          easing="cubicInOut"
        />
      </div>
    </div>

    <div class="p-8 grid grid-rows-subgrid row-span-2">
      <div class="text-sm font-medium text-gray-500">Gemiste interacties</div>
      <div class="text-4xl font-medium mt-2">
        <app-counter
          [target]="numericCards().missed"
          [duration]="1000"
          easing="cubicInOut"
        />
      </div>
    </div>
  </div>

  <!-- Cards that read *along* the period, on a calendar axis. -->
  <section class="flex flex-col gap-3">
    <div class="grid grid-cols-[repeat(auto-fit,minmax(min(500px,100%),1fr))] gap-4">
      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Geplande vs. afgespeelde interacties</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              type="line"
              [datasets]="plannedVsCompletedChartData"
              [labels]="chartLabels"
              [options]="plannedVsCompletedChartOptions"
            ></canvas>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Gebruik signalering</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              [datasets]="scmBlockChartData"
              [labels]="chartLabels"
              [options]="timeblockChartOptions"
            ></canvas>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Afgeronde interacties per dag</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              type="bar"
              [datasets]="completionStatusChartData"
              [labels]="chartLabels"
              [options]="dailyCompletionStatusChartOptions"
            ></canvas>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Afrondings&shy;percentage interacties</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              type="bar"
              [datasets]="completionPercentageChartData"
              [labels]="['Afrondingspercentage interacties']"
              [options]="completionPercentageChartOptions"
            ></canvas>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Afgeronde interacties per dagdeel</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              [datasets]="completedByTimeblockChartData"
              [labels]="chartLabels"
              [options]="timeblockChartOptions"
            ></canvas>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg">
        <div class="flex p-4 border-b border-gray-200 select-none">
          <div class="flex flex-wrap items-center gap-2">
            <h4 class="text-lg font-medium">Online registratie robot</h4>
          </div>
        </div>
        <div class="p-4">
          <div class="aspect-16/9 overflow-hidden">
            <canvas
              baseChart
              [datasets]="robotOnlineByTimeblockChartData"
              [labels]="chartLabels"
              [options]="timeblockChartOptions"
            ></canvas>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- One day at a time, with its own navigation: this section answers "what
       did the client do today, and yesterday", which a period collapsed into a
       single grid cannot. The global chips deliberately do not reach it. -->
  <!-- Ruled off from the period-driven charts above: this section keeps its own
       day and ignores the global chips, so it should not read as more of the
       same block. mt-4 on top of the container's gap-4 makes the space above
       the rule match the pt-8 below it, so the divider sits between the two
       blocks rather than attached to this one. -->
  <section class="flex flex-col gap-4 border-t border-gray-200 mt-4 pt-8">
    <!-- Title and navigation share one row: the day being shown is a property
         of the whole section, not of the card, so the control that changes it
         sits with the heading rather than inside the panel it redraws. -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-xl font-semibold text-gray-900">Signalering per dag</h3>

        <p class="text-sm text-gray-500">Activiteit per dag, uitgesplitst per uur.</p>
      </div>

      <!-- Prev / date / next, then a jump home. The date is the picker itself,
           so a month back is one pick rather than thirty steps. -->
      <div class="flex items-center gap-1 select-none">
        <button
          class="inline-flex!"
          mat-icon-button
          (click)="stepScmDay(-1)"
          [disabled]="!canStepScmDayBack()"
          matTooltip="Vorige dag"
          aria-label="Vorige dag"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>

        <div class="relative">
          <!-- The calendar anchors to this input, not to the button that opens
               it, so the input has to cover the button's box exactly — give it
               a width of its own and the panel floats off to one side. Same
               pattern as the date-range picker above. -->
          <input
            [matDatepicker]="scmDayPicker"
            [value]="scmDay()"
            [min]="minDate"
            [max]="scmMaxDay"
            (dateChange)="selectScmDay($event.value)"
            class="opacity-0 pointer-events-none absolute inset-0"
            tabindex="-1"
            aria-hidden="true"
          />

          <button
            mat-button
            (click)="scmDayPicker.open()"
            class="text-sm!"
            matTooltip="Andere dag kiezen"
            [attr.aria-label]="'Dag kiezen, nu ' + scmDayLabel()"
          >
            <mat-icon class="text-gray-400!">calendar_today</mat-icon>
            {{ scmDayLabel() }}
          </button>

          <mat-datepicker #scmDayPicker [xPosition]="'end'" />
        </div>

        <button
          class="inline-flex!"
          mat-icon-button
          (click)="stepScmDay(1)"
          [disabled]="!canStepScmDayForward()"
          matTooltip="Volgende dag"
          aria-label="Volgende dag"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>

        <button
          class="inline-flex!"
          mat-icon-button
          (click)="goToScmToday()"
          [disabled]="!canStepScmDayForward()"
          matTooltip="Terug naar vandaag"
          aria-label="Terug naar vandaag"
        >
          <mat-icon>today</mat-icon>
        </button>
      </div>
    </div>

    <div class="bg-white rounded-lg p-4">
      <app-scm-click-heatmap [blocks]="scmClickBlocks()" [day]="scmDay()" />
    </div>
  </section>
</div>
